import type { AppDispatch } from "app/store";
import { ErrorResponse, RejectedValue } from "shared/interfaces/core-interfaces";
import { NotificationTypes } from "shared/interfaces/status-notifications-interfaces";
import { showNotification } from "shared/slices/statusNotificationsSlice";

export const GENERIC_ERROR_MESSAGE = "Something went wrong. Please try again later.";

interface RejectApi<R> {
    dispatch: AppDispatch;
    rejectWithValue: (value: RejectedValue) => R;
}

interface RejectOptions {
    /** Show the error as a toast. Off for loads whose screen shows its own error state. */
    notify?: boolean;
}

/** Reads a failed response's ProblemDetails without throwing: the body can be empty or not JSON. */
export async function readErrorResponse(response: Response): Promise<ErrorResponse | null> {
    try {
        const text = await response.text();
        return text ? JSON.parse(text) as ErrorResponse : null;
    }
    catch {
        return null;
    }
}

/** The else branch of a thunk: reject with the backend's message, and show it unless told not to. */
export async function rejectFailedResponse<R>(response: Response, api: RejectApi<R>, options: RejectOptions = {}): Promise<R> {
    const errorResponse = await readErrorResponse(response);
    const message = errorResponse?.detail || GENERIC_ERROR_MESSAGE;
    if (options.notify !== false) {
        api.dispatch(showNotification({ message, type: NotificationTypes.Error }));
    }
    return api.rejectWithValue({ message, status: response.status });
}

/** The catch branch of a thunk: the request never got an answer (offline, CORS, aborted). */
export function rejectNetworkError<R>(api: RejectApi<R>, options: RejectOptions = {}): R {
    if (options.notify !== false) {
        api.dispatch(showNotification({ message: GENERIC_ERROR_MESSAGE, type: NotificationTypes.Error }));
    }
    return api.rejectWithValue({ message: GENERIC_ERROR_MESSAGE });
}
